import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import {
  LANDING_PAGE_ROUTE,
  LANDING_PAGE_SECTION,
  LANDING_PAGE_UI,
  LANDING_PAGE_WORKFLOWS,
  LANDING_PAGE_WORKFLOW_STEP_PREFIX,
} from '@propbix/constants/public'
import { ButtonLink } from '@/components/Button'
import { Reveal } from '@/components/Reveal'
import { tenantAppHref } from '@/lib/tenant-app-url'

const toneClass = {
  ok: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  warn: 'bg-amber-500/12 text-amber-800 dark:text-amber-200',
  info: 'bg-teal-500/12 text-teal-800 dark:text-teal-200',
  danger: 'bg-rose-500/12 text-rose-700 dark:text-rose-300',
  neutral: 'bg-line/10 text-muted',
} as const

export function WorkflowExplorer() {
  const [activeId, setActiveId] = useState(LANDING_PAGE_WORKFLOWS[0].id)
  const active = LANDING_PAGE_WORKFLOWS.find((item) => item.id === activeId) ?? LANDING_PAGE_WORKFLOWS[0]

  return (
    <section
      id={LANDING_PAGE_SECTION.product}
      className="section-band bg-background"
      aria-labelledby="workflows-heading"
    >
      <div className="page-shell section-y">
        <Reveal className="max-w-3xl">
          <p className="section-label">{LANDING_PAGE_UI.productLabel}</p>
          <h2 id="workflows-heading" className="section-title mt-3">
            {LANDING_PAGE_UI.productTitle}
          </h2>
          <p className="section-body mt-5">{LANDING_PAGE_UI.productSubtitle}</p>
        </Reveal>

        <div className="mt-12">
          <div
            className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label={LANDING_PAGE_UI.showcaseHint}
          >
            {LANDING_PAGE_WORKFLOWS.map((item) => {
              const selected = item.id === activeId
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActiveId(item.id)}
                  className={
                    selected
                      ? 'shrink-0 rounded-[var(--radius-control)] bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-sm dark:bg-accent dark:text-[rgb(var(--cta-ink))]'
                      : 'shrink-0 rounded-[var(--radius-control)] border border-line/15 bg-panel px-4 py-2.5 text-sm font-semibold text-muted transition-colors hover:border-line/30 hover:text-ink'
                  }
                >
                  {item.label}
                </button>
              )
            })}
          </div>

          <div
            className="mt-6 overflow-hidden rounded-[var(--radius-desk)] border border-line/10 bg-panel shadow-[var(--shadow-frame)]"
            aria-live="polite"
          >
            <div className="border-b border-line/10 bg-surface/60 px-5 py-4 md:px-7 md:py-5">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted">
                {LANDING_PAGE_UI.workflowLifecycleLabel}
              </p>
              <h3 className="mt-2 font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">
                {active.title}
              </h3>
              <p className="mt-2 max-w-[58ch] text-base leading-7 text-muted">{active.description}</p>
            </div>

            <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
              <div className="border-b border-line/10 p-5 md:p-7 lg:border-b-0 lg:border-r lg:border-line/10">
                <ol className="relative space-y-0">
                  {active.steps.map((step, index) => {
                    const isLast = index === active.steps.length - 1
                    return (
                      <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
                        {!isLast ? (
                          <span
                            className="absolute left-[11px] top-7 bottom-0 w-px bg-gradient-to-b from-accent/40 to-line/15"
                            aria-hidden
                          />
                        ) : null}
                        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center rounded-full border border-accent/30 bg-accentSoft text-[0.65rem] font-bold text-accent dark:bg-accentSoft/40">
                          {index + 1}
                        </span>
                        <div className="min-w-0 pt-0.5">
                          <p className="text-sm font-semibold text-ink">
                            <span className="sr-only">
                              {LANDING_PAGE_WORKFLOW_STEP_PREFIX} {index + 1}.{' '}
                            </span>
                            {step.title}
                          </p>
                          <p className="mt-1 text-sm leading-6 text-muted">{step.detail}</p>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>

              <div className="bg-surface/30 p-5 md:p-7">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted">
                  {LANDING_PAGE_UI.workflowBoardLabel}
                </p>
                <p className="mt-1 text-sm font-semibold text-ink">{active.boardTitle}</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {active.columns.map((column) => (
                    <div key={column.title} className="min-w-0">
                      <p className="mb-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
                        {column.title}
                      </p>
                      <ul className="space-y-2.5">
                        {column.cards.map((card) => (
                          <li
                            key={`${card.title}-${card.meta}`}
                            className="rounded-[var(--radius-card)] border border-line/10 bg-panel p-3.5 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-semibold text-ink">{card.title}</p>
                              <span className={`desk-badge shrink-0 ${toneClass[card.tone]}`}>{card.badge}</span>
                            </div>
                            <p className="mt-1.5 text-xs text-muted">{card.meta}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <ButtonLink variant="primary" href={tenantAppHref(LANDING_PAGE_ROUTE.signup)}>
                    {LANDING_PAGE_UI.showcaseCta}
                    <ArrowRight size={16} strokeWidth={2} />
                  </ButtonLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
